import { useTranslations } from "@jugl-web/utils";
import { BottomCenteredDrawer } from "@jugl-web/ui-components/web/BottomCenteredDrawer";
import { Alert } from "@jugl-web/ui-components";
import { useCallback, useMemo, useState } from "react";
import { SubscriptionIntervalType } from "@jugl-web/rest-api/entities/models/common-types/SubscriptionIntervalType";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { DataLoadingWrapper } from "@jugl-web/ui-components/cross-platform/content/DataLoadingWrapper";
import { comparePlanTiers } from "@web-src/modules/cx/pages/CXPlansPage/utils/planTierUtils";
import { useSelectSubscriptionFormProvider } from "@web-src/modules/workspace/components/SelectSubscriptionForm/useSelectSubscriptionFormProvider";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { getPlanSeatLimit, isDowngradeBlocked } from "./utils/downgradeGuard";
import { DowngradeDisabledTooltip } from "./components/DowngradeDisabledTooltip";
import { PlanCard } from "./components/PlanCard/PlanCard";
import { PlanComparison } from "./components/PlanComparison";
import { DowngradePlanAlertContent } from "./components/DowngradePlanAlertContent";

import { Faq } from "./components/Faq";
import { Banner } from "./components/Banner";
import { SubscriptionIntervals } from "./components/SubscriptionIntervals/SubscriptionIntervals";
import { ClientsShowcase } from "./components/ClientsShowcase/ClientsShowcase";
import { SelectedPlanInfo } from "../../../CompletePurchaseDialog/components/SummaryStep/types";

export const PLANS_STEP_CONTAINER_ID = "plans-step-container";

export const PlansStep: React.FC<{
  onSelectPlan?: (planInfo: SelectedPlanInfo) => void;
  onDowngrade?: (planInfo: SelectedPlanInfo) => void;
  currentPlanId?: string;
  currentInterval?: SubscriptionIntervalType;
}> = ({ onSelectPlan, onDowngrade, currentPlanId, currentInterval }) => {
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();
  const {
    isSubscriptionPendingCancel,
    isSubscriptionCanceled,
    currentPlan,
    seatUsage,
    upgradeSubscriptionPlan,
    hasPaymentMethod,
    updateCardDetails,
  } = useSubscriptionV2();
  const { subscriptionV2Api } = useRestApiProvider();
  const {
    data: plans,
    isLoading,
    isError,
    refetch,
  } = subscriptionV2Api.useListPlansQuery({
    entityId,
    params: { type: "cx" },
  });

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [downgradeDialogState, setDowngradeDialogState] = useState<{
    isOpen: boolean;
    planInfo?: SelectedPlanInfo;
    targetPlanName?: string;
  }>({ isOpen: false });

  const requestDowngrade = useCallback(
    (args: { planInfo: SelectedPlanInfo; targetPlanName: string }) =>
      setDowngradeDialogState({ isOpen: true, ...args }),
    []
  );

  const { selectSubscriptionInterval, selectedSubscriptionInterval } =
    useSelectSubscriptionFormProvider();

  const subscriptionIntervals: {
    label: string;
    value: SubscriptionIntervalType;
    annotation?: string;
  }[] = useMemo(
    () => [
      { label: "Yearly", value: "year" },
      {
        label: "Monthly",
        value: "month",
      },
    ],
    []
  );

  const visiblePlans = useMemo(
    () => plans?.data.filter((item) => item) || [],

    [plans]
  );

  return (
    <BottomCenteredDrawer.Content
      id={PLANS_STEP_CONTAINER_ID}
      className="jugl__hidden-scrollbar relative flex flex-col items-center justify-start overflow-x-auto pb-1"
    >
      <div
        className="w-full pt-[80px] pb-[95px]"
        style={{
          background: `
            url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><filter id="n" x="0" y="0" width="100%" height="100%"><feTurbulence type="fractalNoise" baseFrequency="12.5" numOctaves="20" stitchTiles="stitch"/></filter><rect width="100" height="100" filter="url(%23n)" opacity="0.12"/></svg>'),
            linear-gradient(180deg, #111116 0%, #273785 51%, #FFFFFF00 100%)
          `,
          WebkitMaskImage:
            "linear-gradient(to bottom, #000 0%, #000 80%, transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, #000 0%, #000 80%, transparent 100%)",
        }}
      >
        <div className="w-full px-8 text-center text-[48px] font-semibold text-white">
          {t({
            id: "subscription-plans-dialog.hero-title",
            defaultMessage: "Best solution for Your Business",
          })}
        </div>

        <div className="flex w-full justify-center pt-12">
          <SubscriptionIntervals
            intervals={subscriptionIntervals}
            selectedInterval={selectedSubscriptionInterval}
            onIntervalChange={selectSubscriptionInterval}
          />
        </div>
        <div className="w-full">
          <div className="flex items-center justify-center pt-[14px]">
            <div
              className=" rounded-xl py-1 px-2.5 text-center text-[13px] text-white"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, #1F4CDE 0%, #6678C800 45%,transparent 50%)",
              }}
            >
              {t(
                {
                  id: "subscription-plans-dialog.save-with-annual-billing",
                  defaultMessage:
                    "<b>Save up to {percent}%</b> with annual billing",
                },
                {
                  b: (chunks: React.ReactNode) => (
                    <b className="font-bold">{chunks}</b>
                  ),
                  percent: 18,
                }
              )}
            </div>
          </div>
          <DataLoadingWrapper
            isLoading={isLoading}
            isError={isError}
            onRetry={refetch}
            className="min-h-[400px]"
          >
            <div className="w-full overflow-x-auto px-10 pt-[56px]">
              <div className="mx-auto flex w-fit gap-8">
                {visiblePlans?.map((plan, index) => {
                  const isFree = plan.prices.every((p) => p.unit_amount === 0);
                  const isCurrentPlan =
                    plan.id === currentPlanId &&
                    (!currentInterval ||
                      selectedSubscriptionInterval === currentInterval ||
                      isFree);
                  const currentPlanFromVisiblePlans = currentPlanId
                    ? visiblePlans.find((p) => p.id === currentPlanId)
                    : undefined;
                  const planComparisonResult =
                    currentPlanFromVisiblePlans && !isCurrentPlan
                      ? comparePlanTiers(currentPlanFromVisiblePlans, plan)
                      : undefined;
                  const downgradeDisabled = isDowngradeBlocked({
                    plan,
                    currentPlan: currentPlanFromVisiblePlans,
                    entityUserCount: seatUsage,
                    isSubscriptionCanceled,
                  });

                  return (
                    <PlanCard
                      plan={plan}
                      allPlans={visiblePlans}
                      key={plan.id}
                      name={plan.name}
                      isMostPopular={index === 2}
                      isSelected={
                        selectedPlan === plan.id ||
                        (isCurrentPlan && !selectedPlan)
                      }
                      isCurrentPlan={isCurrentPlan}
                      onSelect={() => {
                        if (!isCurrentPlan) {
                          setSelectedPlan(plan.id);
                        }
                      }}
                      isDowngradeDisabled={downgradeDisabled}
                      downgradeDisabledTooltip={
                        downgradeDisabled ? (
                          <DowngradeDisabledTooltip
                            planName={plan.name}
                            seatLimit={getPlanSeatLimit(plan)}
                          />
                        ) : undefined
                      }
                      onButtonClick={() => {
                        if (!isCurrentPlan || isSubscriptionPendingCancel) {
                          setSelectedPlan(plan.id);
                          if (planComparisonResult === "downgrade") {
                            requestDowngrade({
                              planInfo: {
                                planId: plan.id,
                                period: selectedSubscriptionInterval,
                              },
                              targetPlanName: plan.name,
                            });
                          } else {
                            onSelectPlan?.({
                              planId: plan.id,
                              period: selectedSubscriptionInterval,
                            });
                          }
                        }
                      }}
                      type="project"
                      interval={selectedSubscriptionInterval}
                    />
                  );
                })}
              </div>
            </div>
          </DataLoadingWrapper>
        </div>
      </div>
      <PlanComparison
        currentPlanId={currentPlanId}
        plans={visiblePlans}
        selectedSubscriptionInterval={selectedSubscriptionInterval}
        entityUserCount={seatUsage}
        isSubscriptionCanceled={isSubscriptionCanceled}
        onSelectPlan={onSelectPlan}
        onRequestDowngrade={requestDowngrade}
        onPlanSelect={setSelectedPlan}
      />
      <Faq />
      <div className="flex w-full justify-center py-[140px] px-8">
        <Banner />
      </div>
      <div className="max-w-full pb-20">
        <ClientsShowcase />
      </div>
      <Alert
        isOpen={downgradeDialogState.isOpen}
        onRequestClose={() =>
          setDowngradeDialogState((prev) => ({ ...prev, isOpen: false }))
        }
        title={t({
          id: "subscription-plans-dialog.downgrade-plan-title",
          defaultMessage: "⚠️ Downgrade Plan",
        })}
        contentContainerClassName="px-10 pt-10 pb-10"
        content={
          <DowngradePlanAlertContent
            targetPlanName={downgradeDialogState.targetPlanName ?? ""}
            currentPlanName={currentPlan?.name ?? ""}
            onCancel={() =>
              setDowngradeDialogState((prev) => ({ ...prev, isOpen: false }))
            }
            onDowngrade={async () => {
              setDowngradeDialogState((prev) => ({ ...prev, isOpen: false }));
              if (!downgradeDialogState.planInfo) return;
              const { planId, period } = downgradeDialogState.planInfo;
              const targetPlan = visiblePlans.find((p) => p.id === planId);
              if (!targetPlan) return;

              const isFreeTarget = targetPlan.prices.every(
                (p) => p.unit_amount === 0
              );

              if (!isFreeTarget) {
                // Paid plan downgrade → open summary screen
                onDowngrade?.(downgradeDialogState.planInfo);
                return;
              }

              // Free plan downgrade → process directly
              const priceObj = targetPlan.prices.find(
                (p) => p.interval === period
              );
              if (!priceObj) return;

              if (!hasPaymentMethod) {
                const cardResult = await updateCardDetails();
                if (cardResult.result !== "success") return;
              }

              try {
                await upgradeSubscriptionPlan({
                  planId,
                  priceId: priceObj.id,
                  planName: targetPlan.name,
                  onSuccess: () => {
                    sessionStorage.setItem(
                      "onboarding_flow_pending",
                      JSON.stringify({
                        flow: "planDowngraded",
                        planName: targetPlan.name,
                        previousPlanName: currentPlan?.name ?? "",
                      })
                    );
                    window.location.reload();
                  },
                });
              } catch {
                // Provider shows payment failed alert
              }
            }}
          />
        }
      />
    </BottomCenteredDrawer.Content>
  );
};
